import { createClient } from '@supabase/supabase-js';

/**
 * Shared Supabase client for the onboarding web app.
 *
 * Lazy singleton: created once on first access and reused.
 *
 * Config comes from window globals rendered by the Django template
 * (index.html_template), which the backend populates from get_settings
 * ("supabase_url" / "supabase_anon_key"). The publishable (anon) key is NOT a
 * secret — it is designed to be public and embedded in client-side code.
 * See: https://supabase.com/docs/guides/api/api-keys
 *
 * Auth options follow TDD-nhs-login-onboarding-migration §4.2:
 * - storage: sessionStorage — the PKCE code_verifier must survive the NHS
 *   redirect within the tab; sessionStorage is cleared when the tab closes.
 *   persistSession is left at its default (true) on purpose: supabase-js keeps
 *   the code_verifier in the same storage as the session, so disabling
 *   persistence would drop the verifier and the code exchange would fail.
 * - flowType: 'pkce' — the login flow exchanges an auth code for the session.
 * - detectSessionInUrl: false — the app handles the callback and runs the
 *   exchange itself.
 * - autoRefreshToken: false — the Supabase session is a transient handoff
 *   credential; the Big Health session stays authoritative.
 */

let client = null;

export const getSupabaseClient = () => {
  if (!client) {
    // Read at call time (not import time): the window globals are rendered by
    // the Django template and are guaranteed to be set before the app calls in.
    client = createClient(window.supabase_url, window.supabase_anon_key, {
      auth: {
        storage: window.sessionStorage,
        flowType: 'pkce',
        detectSessionInUrl: false,
        autoRefreshToken: false,
      },
    });
  }

  return client;
};

export const resetSupabaseClient = () => {
  client = null;
};
